import React, { useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Alert,
  Chip,
} from '@mui/material';
import { RiDownloadLine, RiCheckLine } from '@remixicon/react';
import { toPng } from 'html-to-image';
import { setVideoCurrentTime, setVideoPlaying, setVideoDuration } from '../redux/slices/canvasSlice';
import axios from 'axios';

const PRESIGNED_URL_API = 'https://us-central1-digislidesapp.cloudfunctions.net/s3GeneratePresignedUrl';
const PROXY_IMAGE_API = 'https://us-central1-digislidesapp.cloudfunctions.net/s3ProxyImage';

/**
 * Extract S3 key from a full S3 URL
 */
const extractS3Key = (url) => {
  try {
    const urlObj = new URL(url);
    if (urlObj.hostname.includes('s3') || urlObj.hostname.includes('amazonaws.com')) {
      return urlObj.pathname.substring(1);
    }
    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Convert image URL to data URL (handles CORS by fetching as blob)
 */
const imageToDataUrl = async (url) => {
  try {
    // If it's already a data URL, return it
    if (url.startsWith('data:')) {
      return url;
    }

    // Check if it's an S3 URL
    const s3Key = extractS3Key(url);
    let imageUrl = url;
    
    // Use proxy API to fetch image server-side (bypasses CORS)
    if (s3Key) {
      try {
        const response = await axios.post(
          PROXY_IMAGE_API,
          { s3Key: s3Key },
          {
            headers: {
              'Content-Type': 'application/json',
            },
            timeout: 15000, // 15 second timeout
          }
        );
        
        if (response.data?.dataUrl) {
          return response.data.dataUrl;
        }
      } catch (proxyError) {
        console.warn('Proxy API failed, trying direct fetch:', proxyError);
      }
    }

    // Fallback: Try direct fetch (may fail due to CORS)
    try {
      const response = await axios.get(imageUrl, {
        responseType: 'blob',
        timeout: 10000,
      });
      
      const blob = response.data;
      
      // Convert blob to data URL
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve(reader.result);
        };
        reader.onerror = () => {
          console.warn('Failed to read blob as data URL');
          loadImageWithoutCORS(imageUrl, resolve);
        };
        reader.readAsDataURL(blob);
      });
    } catch (fetchError) {
      console.warn('Direct fetch failed, trying image load without CORS:', fetchError);
      // Final fallback: try loading as image without CORS
      return new Promise((resolve) => {
        loadImageWithoutCORS(imageUrl, resolve);
      });
    }
  } catch (error) {
    console.warn('Error converting image to data URL:', error);
    return url; // Fallback to original URL
  }
};

/**
 * Fallback: Load image without CORS (may fail for canvas conversion)
 */
const loadImageWithoutCORS = (imageUrl, resolve) => {
  const img = new Image();
  // Don't set crossOrigin - this may work for some images
  
  img.onload = () => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      const dataUrl = canvas.toDataURL('image/png');
      resolve(dataUrl);
    } catch (error) {
      console.warn('Failed to convert image to data URL (CORS issue):', error);
      resolve(imageUrl); // Fallback to original URL
    }
  };
  
  img.onerror = () => {
    console.warn('Failed to load image:', imageUrl);
    resolve(imageUrl); // Fallback to original URL
  };
  
  img.src = imageUrl;
};

/**
 * Convert all images in the canvas to data URLs
 */
const convertImagesToDataUrls = async (element) => {
  const images = element.querySelectorAll('img');
  const imageMap = new Map();
  
  for (const img of images) {
    const src = img.getAttribute('src');
    if (src && !src.startsWith('data:')) {
      try {
        const dataUrl = await imageToDataUrl(src);
        imageMap.set(img, { originalSrc: src, dataUrl });
        img.setAttribute('src', dataUrl);
      } catch (error) {
        console.warn('Failed to convert image:', src, error);
      }
    }
  }
  
  return imageMap;
};

/**
 * Restore original image sources
 */
const restoreImageSources = (imageMap) => {
  imageMap.forEach(({ originalSrc }, img) => {
    img.setAttribute('src', originalSrc);
  });
};

const ExportDialog = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const { orientation, designMode, elements, background, videoTime, dimensions } = useSelector(
    (state) => state.canvas
  );
  
  const [exportFormat, setExportFormat] = useState('png');
  const [fileName, setFileName] = useState('menu-design');
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportError, setExportError] = useState(null);
  const [exportSuccess, setExportSuccess] = useState(false);
  const canvasRef = useRef(null);
  
  // Find the canvas element
  const getCanvasElement = () => {
    return document.querySelector('#canvas-background') || 
           document.querySelector('[data-canvas="true"]') ||
           document.querySelector('.MuiPaper-root[style*="position: relative"]');
  };

  const handleExportPNG = async () => {
    let imageMap = new Map();
    
    try {
      setExportProgress(10);
      const canvasElement = getCanvasElement();
      
      if (!canvasElement) {
        throw new Error('Canvas element not found');
      }

      setExportProgress(20);
      
      // Convert all images to data URLs to avoid CORS issues
      imageMap = await convertImagesToDataUrls(canvasElement);
      setExportProgress(40);
      
      // Get canvas dimensions
      const canvasDimensions = dimensions[orientation];
      
      // Export as PNG using html-to-image
      const dataUrl = await toPng(canvasElement, {
        width: canvasDimensions.width,
        height: canvasDimensions.height,
        style: {
          transform: 'scale(1)',
          transformOrigin: 'top left',
        },
        pixelRatio: 2, // Higher quality
        backgroundColor: background.type === 'color' ? background.value : '#FFFFFF',
        useCORS: false, // Disable CORS since we've already converted images to data URLs
        cacheBust: true, // Bust cache for images
        fontEmbedCSS: '', // Disable font embedding to avoid CSS rules errors
      });

      setExportProgress(90);

      // Restore original image sources
      restoreImageSources(imageMap);

      // Download the image
      const link = document.createElement('a');
      link.download = `${fileName || 'export'}.png`;
      link.href = dataUrl;
      link.click();

      setExportProgress(100);
      setExportSuccess(true);
      
      setTimeout(() => {
        onClose();
        setExportSuccess(false);
        setExportProgress(0);
      }, 2000);
    } catch (error) {
      console.error('PNG export error:', error);
      // Restore images even on error
      restoreImageSources(imageMap);
      setExportError(error.message || 'Failed to export PNG. Please try again.');
      setExportProgress(0);
    }
  };

  const handleExportMP4 = async () => {
    try {
      setExportProgress(5);
      
      const canvasElement = getCanvasElement();
      if (!canvasElement) {
        throw new Error('Canvas element not found');
      }

      // Get canvas dimensions
      const canvasDimensions = dimensions[orientation];
      const duration = videoTime.duration || 30;
      const fps = 30;
      const totalFrames = Math.ceil(duration * fps);
      
      // Create a canvas for recording
      const canvas = document.createElement('canvas');
      canvas.width = canvasDimensions.width;
      canvas.height = canvasDimensions.height;
      const ctx = canvas.getContext('2d');

      setExportProgress(10);

      // Store original video state
      const originalTime = videoTime.currentTime;
      const originalPlaying = videoTime.isPlaying;

      // Stop video if playing
      if (videoTime.isPlaying) {
        dispatch(setVideoPlaying(false));
      }

      // Convert all images to data URLs once before capturing frames
      setExportProgress(15);
      const imageMap = await convertImagesToDataUrls(canvasElement);
      setExportProgress(20);

      // Capture frames
      const frames = [];
      const frameDelay = 1000 / fps;

      for (let frame = 0; frame < totalFrames; frame++) {
        const currentTime = (frame / fps);
        dispatch(setVideoCurrentTime(currentTime));
        
        // Wait for React to update DOM and animations to render
        // Use requestAnimationFrame to ensure the frame is rendered
        await new Promise(resolve => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              setTimeout(resolve, 50); // Additional small delay for animations
            });
          });
        });

        // Capture frame
        const dataUrl = await toPng(canvasElement, {
          width: canvasDimensions.width,
          height: canvasDimensions.height,
          pixelRatio: 1,
          backgroundColor: background.type === 'color' ? background.value : '#FFFFFF',
          useCORS: false, // Disable CORS since we've already converted images to data URLs
          cacheBust: true,
          fontEmbedCSS: '', // Disable font embedding to avoid CSS rules errors
        });

        frames.push(dataUrl);
        setExportProgress(20 + (frame / totalFrames) * 70);
      }

      // Restore original image sources
      restoreImageSources(imageMap);

      setExportProgress(90);

      // Restore original state
      dispatch(setVideoCurrentTime(originalTime));
      dispatch(setVideoPlaying(originalPlaying));

      // Convert frames to video using MediaRecorder
      await createVideoFromFrames(frames, canvasDimensions, fps, fileName);

      setExportProgress(100);
      setExportSuccess(true);
      
      setTimeout(() => {
        onClose();
        setExportSuccess(false);
        setExportProgress(0);
      }, 2000);
    } catch (error) {
      console.error('MP4 export error:', error);
      setExportError(error.message || 'Failed to export MP4. Please try again.');
      setExportProgress(0);
      
      // Restore original state on error
      dispatch(setVideoPlaying(false));
    }
  };

  const createVideoFromFrames = async (frames, dimensions, fps, fileName) => {
    return new Promise((resolve, reject) => {
      try {
        // Create a canvas for video frames
        const canvas = document.createElement('canvas');
        canvas.width = dimensions.width;
        canvas.height = dimensions.height;
        const ctx = canvas.getContext('2d');

        // Create image elements for frames
        const images = frames.map(src => {
          const img = new Image();
          img.src = src;
          return img;
        });

        // Wait for all images to load
        Promise.all(images.map(img => new Promise(resolve => {
          if (img.complete) resolve();
          else img.onload = resolve;
        }))).then(() => {
          // Use MediaRecorder API
          const stream = canvas.captureStream(fps);
          const mediaRecorder = new MediaRecorder(stream, {
            mimeType: 'video/webm;codecs=vp9',
          });

          const chunks = [];
          mediaRecorder.ondataavailable = (e) => {
            if (e.data.size > 0) chunks.push(e.data);
          };

          mediaRecorder.onstop = () => {
            const blob = new Blob(chunks, { type: 'video/webm' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.download = `${fileName || 'export'}.mp4`;
            link.href = url;
            link.click();
            URL.revokeObjectURL(url);
            resolve();
          };

          // Draw frames and record
          let frameIndex = 0;
          mediaRecorder.start();

          const drawFrame = () => {
            if (frameIndex >= images.length) {
              mediaRecorder.stop();
              return;
            }

            ctx.drawImage(images[frameIndex], 0, 0);
            frameIndex++;
            setTimeout(drawFrame, 1000 / fps);
          };

          drawFrame();
        }).catch(reject);
      } catch (error) {
        // Fallback: Download as WebM (browser may convert)
        reject(error);
      }
    });
  };

  // Backend export API URL
  const BACKEND_EXPORT_API = process.env.REACT_APP_EXPORT_API || 'http://localhost:5001/api/export';

  const handleExport = async () => {
    setIsExporting(true);
    setExportError(null);
    setExportProgress(0);
    
    try {
      // Try backend export first (if available)
      const useBackend = process.env.REACT_APP_USE_BACKEND_EXPORT !== 'false';
      
      if (useBackend) {
        try {
          await handleBackendExport();
          return;
        } catch (backendError) {
          console.warn('Backend export failed, falling back to client-side:', backendError);
          // Fall through to client-side export
        }
      }

      // Client-side export (fallback)
      if (exportFormat === 'png') {
        await handleExportPNG();
      } else if (exportFormat === 'mp4') {
        await handleExportMP4();
      }
    } catch (error) {
      console.error('Export error:', error);
      setExportError(error.message || 'Failed to export. Please try again.');
      setExportProgress(0);
    } finally {
      setIsExporting(false);
    }
  };

  const handleBackendExport = async () => {
    setExportProgress(10);

    // Prepare canvas data
    const canvasData = {
      orientation,
      designMode,
      dimensions,
      background,
      elements,
      videoTime,
    };

    setExportProgress(30);

    // Call backend API
    const endpoint = exportFormat === 'png' ? `${BACKEND_EXPORT_API}/png` : `${BACKEND_EXPORT_API}/mp4`;
    const response = await axios.post(
      endpoint,
      {
        canvasData,
        fileName: fileName || 'export',
        fps: 30,
      },
      {
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 90) / progressEvent.total) + 10;
            setExportProgress(progress);
          }
        },
        timeout: 300000, // 5 minutes timeout for video export
      }
    );

    setExportProgress(95);

    // Download the file
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName || 'export'}.${exportFormat}`;
    link.click();
    window.URL.revokeObjectURL(url);

    setExportProgress(100);
    setExportSuccess(true);

    setTimeout(() => {
      onClose();
      setExportSuccess(false);
      setExportProgress(0);
    }, 2000);
  };
  
  const getRecommendedFormat = () => {
    if (designMode === 'video') return 'mp4';
    
    // Check if any element has animations
    const hasAnimations = elements.some(
      (el) => el.animations && el.animations.length > 0
    );
    
    if (hasAnimations) return 'mp4';
    return 'png';
  };
  
  const recommendedFormat = getRecommendedFormat();
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Export Design</DialogTitle>
      <DialogContent>
        {exportSuccess ? (
          <Box sx={{ textAlign: 'center', padding: 3 }}>
            <RiCheckLine size={64} color="#10B981" />
            <Typography variant="h6" color="success.main" sx={{ marginTop: 2 }}>
              Export Successful!
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ marginTop: 1 }}>
              Your {exportFormat === 'png' ? 'image' : 'video'} has been downloaded
            </Typography>
          </Box>
        ) : (
          <>
            {exportError && (
              <Alert severity="error" sx={{ marginBottom: 2 }}>
                {exportError}
              </Alert>
            )}
            
            {recommendedFormat !== exportFormat && (
              <Alert severity="info" sx={{ marginBottom: 2 }}>
                Recommended format for this design: <strong>{recommendedFormat.toUpperCase()}</strong>
                {designMode === 'video' || elements.some(el => el.animations?.length > 0)
                  ? ' (contains animations or video)'
                  : ' (static design)'}
              </Alert>
            )}
            
            <TextField
              label="File Name"
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
              fullWidth
              margin="normal"
              disabled={isExporting}
            />
            
            <FormControl fullWidth margin="normal" disabled={isExporting}>
              <InputLabel>Export Format</InputLabel>
              <Select
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value)}
                label="Export Format"
              >
                <MenuItem value="png">PNG Image (Static)</MenuItem>
                <MenuItem value="mp4">MP4 Video (Animated) - Exports as WebM</MenuItem>
              </Select>
            </FormControl>
            
            {exportFormat === 'mp4' && (
              <Alert severity="info" sx={{ marginTop: 2 }}>
                Video will be exported as WebM format (compatible with all modern browsers). 
                You can convert it to MP4 using online tools if needed.
              </Alert>
            )}
            
            <Box sx={{ marginTop: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Export Details
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, marginTop: 1 }}>
                <Chip
                  label={`${orientation === 'landscape' ? '1920×1080' : '1080×1920'}`}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={orientation.charAt(0).toUpperCase() + orientation.slice(1)}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={designMode.charAt(0).toUpperCase() + designMode.slice(1)}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`${elements.length} elements`}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Box>
            
            {isExporting && (
              <Box sx={{ marginTop: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Exporting... {exportProgress}%
                </Typography>
                <LinearProgress variant="determinate" value={exportProgress} />
              </Box>
            )}
          </>
        )}
      </DialogContent>
      {!exportSuccess && (
        <DialogActions>
          <Button onClick={onClose} disabled={isExporting}>
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            variant="contained"
            startIcon={<RiDownloadLine size={18} />}
            disabled={isExporting || !fileName}
          >
            Export
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ExportDialog;